const DESKTOP_MEDIA_QUERY = '(min-width: 750px)';
const HOVER_MEDIA_QUERY = '(hover: hover) and (pointer: fine)';
const CLOSE_DELAY_MS = 140;

class NaturloomMegaMenu {
  /**
   * @param {HTMLElement} root
   */
  constructor(root) {
    this.root = root;
    this.desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);
    this.hoverMedia = window.matchMedia(HOVER_MEDIA_QUERY);
    this.activeTrigger = null;
    this.activePanel = null;
    this.closeTimer = undefined;
    this.restoringTriggerFocus = false;

    this.root.addEventListener('pointerover', this.onPointerOver);
    this.root.addEventListener('pointerenter', this.cancelClose);
    this.root.addEventListener('pointerleave', this.scheduleClose);
    this.root.addEventListener('focusin', this.onFocusIn);
    this.root.addEventListener('focusout', this.onFocusOut);
    this.root.addEventListener('click', this.onClick);
    document.addEventListener('pointerdown', this.onDocumentPointerDown);
    document.addEventListener('keydown', this.onDocumentKeyDown);
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, true);
  }

  onPointerOver = (event) => {
    if (!this.desktopMedia.matches || !this.hoverMedia.matches) return;
    const trigger = event.target instanceof Element ? event.target.closest('[data-naturloom-trigger]') : null;
    if (trigger instanceof HTMLButtonElement && this.root.contains(trigger)) this.open(trigger);
  };

  onFocusIn = (event) => {
    if (!this.desktopMedia.matches) return;
    if (this.restoringTriggerFocus) return;
    const trigger = event.target instanceof Element ? event.target.closest('[data-naturloom-trigger]') : null;
    if (trigger instanceof HTMLButtonElement && this.root.contains(trigger)) this.open(trigger);
  };

  onFocusOut = (event) => {
    const nextTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    if (
      nextTarget &&
      ((this.activeTrigger && this.activeTrigger.contains(nextTarget)) ||
        (this.activePanel && this.activePanel.contains(nextTarget)))
    ) {
      this.cancelClose();
      return;
    }

    requestAnimationFrame(() => {
      const activeElement = document.activeElement;
      if (
        activeElement instanceof Node &&
        ((this.activeTrigger && this.activeTrigger.contains(activeElement)) ||
          (this.activePanel && this.activePanel.contains(activeElement)))
      ) {
        this.cancelClose();
        return;
      }
      this.scheduleClose();
    });
  };

  onClick = (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-naturloom-trigger]') : null;
    if (!(trigger instanceof HTMLButtonElement) || !this.root.contains(trigger) || !this.desktopMedia.matches) return;
    event.preventDefault();
    this.open(trigger);
  };

  onDocumentPointerDown = (event) => {
    if (this.activePanel && event.target instanceof Node && !this.root.contains(event.target)) this.close(false);
  };

  onDocumentKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.activePanel) return;
    event.preventDefault();
    this.close(true);
  };

  onViewportChange = () => {
    if (!this.desktopMedia.matches) {
      this.close(false);
      return;
    }
    this.updatePanelTop();
  };

  scheduleClose = () => {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => this.close(false), CLOSE_DELAY_MS);
  };

  cancelClose = () => {
    window.clearTimeout(this.closeTimer);
    this.closeTimer = undefined;
  };

  /**
   * @param {HTMLButtonElement} trigger
   */
  open(trigger) {
    if (!this.desktopMedia.matches) return;

    const panelId = trigger.getAttribute('aria-controls');
    const panel = panelId ? document.getElementById(panelId) : null;
    if (!(panel instanceof HTMLElement)) return;

    this.cancelClose();

    if (this.activeTrigger && this.activeTrigger !== trigger) {
      this.activeTrigger.setAttribute('aria-expanded', 'false');
    }
    if (this.activePanel && this.activePanel !== panel) {
      this.activePanel.hidden = true;
    }

    this.activeTrigger = trigger;
    this.activePanel = panel;
    trigger.setAttribute('aria-expanded', 'true');
    panel.hidden = false;
    this.root.dataset.naturloomMenuOpen = panelId;
    this.updatePanelTop();
  }

  updatePanelTop() {
    if (!this.activePanel) return;
    const header = this.root.closest('#header-component');
    if (!(header instanceof HTMLElement)) return;
    const headerBottom = Math.max(0, header.getBoundingClientRect().bottom);
    this.activePanel.style.setProperty('--naturloom-mega-panel-top', `${headerBottom}px`);
  }

  /**
   * @param {boolean} returnFocus
   */
  close(returnFocus) {
    this.cancelClose();
    const trigger = this.activeTrigger;

    this.root.querySelectorAll('[data-naturloom-trigger]').forEach((item) => {
      item.setAttribute('aria-expanded', 'false');
    });
    this.root.querySelectorAll('[data-naturloom-panel]').forEach((panel) => {
      if (panel instanceof HTMLElement) panel.hidden = true;
    });

    delete this.root.dataset.naturloomMenuOpen;
    this.activeTrigger = null;
    this.activePanel = null;

    if (returnFocus && trigger) {
      this.restoringTriggerFocus = true;
      trigger.focus();
      this.restoringTriggerFocus = false;
    }
  }
}

document.querySelectorAll('[data-naturloom-menu-root]').forEach((root) => {
  if (root instanceof HTMLElement) new NaturloomMegaMenu(root);
});
